// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../../../../types/react.d.ts" />

'use client';

import type { PropsWithChildren, ReactElement} from 'react';
import { useMemo, useState } from 'react';

import './globals.scss';
import classNames from 'classnames';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { SwiperOptions } from 'swiper/types';

import type { CssMeasure } from '@utilities/helpers/css';
import type { OneOrMany } from '@utilities/helpers/OneOrMany';

import styles from './styles.module.scss';

type SlideWidth = number | CssMeasure;

type GalleryProps = Pick<SwiperOptions, 'autoplay' | 'speed'> & {
  slideAspectRatio?: number;
  slidesOffsetBefore?: number;
  slidesOffsetAfter?: number;
  spacing?: CssMeasure;
  centerSlides?: boolean;
  loopSlides?: boolean;
  fitSlideWidthToContent?: boolean;
  contentPadding?: number;
  breakout?: boolean,
  slideWidth?: SlideWidth | undefined,
  numberOfSlides?: number,
  children?: OneOrMany<ReactElement<GallerySlideProps, typeof GallerySlide>>;
};

const Gallery = ({
  slideAspectRatio,
  slidesOffsetBefore,
  slidesOffsetAfter,
  spacing,
  centerSlides,
  loopSlides,
  children = [],
  speed = 300,
  autoplay = false,
  contentPadding,
  breakout,
  numberOfSlides,
  slideWidth,
}: GalleryProps) => {
const [paginationContainer, setPaginationContainer] = useState<HTMLDivElement | null>(null);

const slides = useMemo(() => {
  return Array.isArray(children) ? children : [children];
}, [children]);

const modules = useMemo(() => {
  const result = [Pagination];

  if (autoplay) {
    result.push(Autoplay);
  }

  return result;
}, [autoplay]);

return (
  <div
    className={classNames(styles['gallery'], { [styles['gallery--breakout']]: breakout })}
    data-aspect-ratio={slideAspectRatio}
    style={{
      '--gallery-spacing': typeof spacing === 'string' ? spacing : `${spacing ?? 0}px`,
      '--gallery-number-of-slides': numberOfSlides,
      paddingLeft: contentPadding && `${contentPadding}px`,
      paddingRight: contentPadding && `${contentPadding}px`,
    }}
  >
    <Swiper
      slidesPerView={numberOfSlides ?? 'auto'}
      spaceBetween={spacing}
      slidesOffsetBefore={slidesOffsetBefore}
      slidesOffsetAfter={slidesOffsetAfter}
      centeredSlides={centerSlides ?? !loopSlides}
      modules={modules}
      watchOverflow={true}
      loop={loopSlides}
      autoplay={autoplay}
      speed={speed}
      pagination={
        !!paginationContainer && {
          clickable: true,
          bulletClass: styles['gallery__pagination__bullet'],
          bulletActiveClass: styles['gallery__pagination__bullet__active'],
          lockClass: styles['gallery__pagination__container__disabled'],
          el: paginationContainer,
        }
      }
      className={styles['gallery__swiper']}
      //  The key is necesary to force re-initialization of the component
      //  when certain properties change
      key={`${loopSlides} ${slideWidth}`}
    >
      {slides.map(({ props }, index) => {
        return (
          <SwiperSlide
            style={{
              width: typeof slideWidth === 'string' ? slideWidth : undefined,
              aspectRatio: slideAspectRatio,
              maxWidth: props.fitWidthToContent ? 'fit-content' : undefined,
              height: props.stretchHeight ? 'auto' : undefined,
            }}
            key={index}
          >
            {props.children}
          </SwiperSlide>
        );
      })}
    </Swiper>

    <div
      className={classNames(styles['gallery__pagination__container'])}
      ref={setPaginationContainer}
    ></div>
  </div>
);
};

interface GallerySlideProps extends PropsWithChildren {
  fitWidthToContent?: boolean;
  stretchHeight?: boolean;
}

const GallerySlide = (props: GallerySlideProps) => {
  return null;
};

Gallery.Slide = GallerySlide;

export { Gallery, GallerySlide, type GalleryProps, type GallerySlideProps };
